// Meal planner + workout builder generators
import type { ShredResults } from './shred'

export interface Meal {
  name: string
  tag: string
  calories: number
  protein: number
  items: string[]
}

// Generate low-calorie, high-protein meal blueprint scaled to calorie goal
export function generateMeals(r: ShredResults): Meal[] {
  const cals = r.dailyCalories
  const protein = r.protein

  // Distribution across 4 meals
  const dist = [0.3, 0.3, 0.25, 0.15]
  const proteinDist = [0.3, 0.3, 0.25, 0.15]

  const templates: Omit<Meal, 'calories' | 'protein'>[] = [
    {
      name: 'High-Protein Breakfast',
      tag: 'Get Ripped Fuel',
      items: ['Egg whites + 2 whole eggs', 'Oats with berries', 'Black coffee'],
    },
    {
      name: 'Lean Power Lunch',
      tag: 'Best Meals to Get Ripped',
      items: ['Grilled chicken breast', 'Jasmine rice', 'Steamed broccoli & greens'],
    },
    {
      name: 'Shredding Dinner',
      tag: 'Food to Get Ripped',
      items: ['Wild salmon or lean steak', 'Sweet potato', 'Asparagus & mixed salad'],
    },
    {
      name: 'Recovery Snack',
      tag: 'Lean & Ripped',
      items: ['Greek yogurt or casein shake', 'Almonds', 'Whey protein scoop'],
    },
  ]

  return templates.map((t, i) => ({
    ...t,
    calories: Math.round(cals * dist[i]),
    protein: Math.round(protein * proteinDist[i]),
  }))
}

export type SplitType = 'ppl' | 'fullbody' | 'upperlower'

export interface WorkoutDay {
  day: string
  focus: string
  exercises: { name: string; scheme: string }[]
}

export const SPLIT_LABELS: Record<SplitType, string> = {
  ppl: 'Push / Pull / Legs',
  fullbody: 'Full Body',
  upperlower: 'Upper / Lower',
}

export function generateWorkout(split: SplitType): WorkoutDay[] {
  if (split === 'ppl') {
    return [
      {
        day: 'Day 1',
        focus: 'Push — Chest, Shoulders, Triceps',
        exercises: [
          { name: 'Incline Dumbbell Press', scheme: '4 x 8-10' },
          { name: 'Seated Shoulder Press', scheme: '3 x 10-12' },
          { name: 'Cable Fly', scheme: '3 x 12-15' },
          { name: 'Triceps Rope Pushdown', scheme: '4 x 12-15' },
        ],
      },
      {
        day: 'Day 2',
        focus: 'Pull — Back & Biceps (Bicep Shredder)',
        exercises: [
          { name: 'Weighted Pull-Ups', scheme: '4 x 6-8' },
          { name: 'Barbell Row', scheme: '4 x 8-10' },
          { name: 'Incline Dumbbell Curl', scheme: '3 x 10-12' },
          { name: 'Cable Hammer Curl (Bicep Shredder)', scheme: '3 x 12-15' },
        ],
      },
      {
        day: 'Day 3',
        focus: 'Legs — Quads, Hamstrings, Calves',
        exercises: [
          { name: 'Barbell Back Squat', scheme: '4 x 6-8' },
          { name: 'Romanian Deadlift', scheme: '3 x 8-10' },
          { name: 'Walking Lunges', scheme: '3 x 12 / leg' },
          { name: 'Standing Calf Raise', scheme: '4 x 15-20' },
        ],
      },
    ]
  }
  if (split === 'upperlower') {
    return [
      {
        day: 'Day 1',
        focus: 'Upper — Metabolic Resistance',
        exercises: [
          { name: 'Bench Press', scheme: '4 x 8-10' },
          { name: 'Lat Pulldown', scheme: '4 x 10-12' },
          { name: 'Lateral Raise Superset Curl', scheme: '3 x 15' },
          { name: 'Face Pull', scheme: '3 x 15-20' },
        ],
      },
      {
        day: 'Day 2',
        focus: 'Lower — Quads & Posterior Chain',
        exercises: [
          { name: 'Front Squat', scheme: '4 x 8-10' },
          { name: 'Hip Thrust', scheme: '3 x 10-12' },
          { name: 'Leg Press', scheme: '3 x 12-15' },
          { name: 'Hanging Leg Raise', scheme: '4 x 15' },
        ],
      },
    ]
  }
  return [
    {
      day: 'Day 1 / 2 / 3',
      focus: 'Full Body — Metabolic Circuit',
      exercises: [
        { name: 'Deadlift', scheme: '4 x 6-8' },
        { name: 'Incline Press', scheme: '3 x 10-12' },
        { name: 'Pull-Ups', scheme: '3 x AMRAP' },
        { name: 'Goblet Squat → Curl Superset', scheme: '3 x 12-15' },
      ],
    },
  ]
}
